import { useCallback, useState } from 'react';
import { useAuth } from '../../../auth/AuthContext';
import { todayCalendarDate } from '../../../components/calendar/monthGrid';
import { ApiError } from '../../../lib/apiError';
import { deliberationApi } from './deliberationApi';
import type { ApplicantDeliberation } from './deliberationTypes';

/**
 * The ENDORSEMENT form — Endorsed by (a picked account), Endorsed at (a picked date), Remarks, and one
 * optional Attachment (product call, 21 Oct 2026). ONE hook behind both of its surfaces, the row
 * kebab's popup and the deliberation modal's Endorsement panel, so the two cannot ask different things.
 *
 * A first endorsement opens on the signed-in account and today, so it opens answerable — Screening's
 * "Screened by" shape. A correction opens on what is stored, and its attachment stays unless a new one
 * is chosen.
 */
export function useEndorsementForm({ postingId, applicantId }: { postingId: string; applicantId: string }) {
  const { user } = useAuth();

  const [endorsedBy, setEndorsedBy] = useState<string | null>(user?.id ?? null);
  const [endorsedDate, setEndorsedDate] = useState(todayCalendarDate());
  const [remarks, setRemarks] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const valid = endorsedBy !== null && endorsedDate !== '';

  /** Seeds the form — from a stored endorsement to correct it, or fresh for a first one. */
  const seed = useCallback(
    (current: ApplicantDeliberation | null) => {
      const stored = current?.isEndorsed ? current : null;
      setEndorsedBy(stored?.endorsedByUserId ?? user?.id ?? null);
      setEndorsedDate(stored?.endorsedDate ?? todayCalendarDate());
      setRemarks(stored?.endorsementRemarks ?? '');
      setFiles([]);
      setError(null);
    },
    [user?.id],
  );

  /**
   * The endorsement first, then the document — the file rides on its own multipart request, which
   * the server only accepts once the applicant IS endorsed. A failed upload leaves the endorsement
   * saved and says so, rather than pretending nothing happened.
   */
  async function submit(): Promise<
    { saved: ApplicantDeliberation; fileFailed: string | null } | { failure: ApiError } | null
  > {
    if (!valid || saving || endorsedBy === null) return null;

    setSaving(true);
    setError(null);

    try {
      let saved = await deliberationApi.endorse(postingId, applicantId, {
        endorsedByUserId: endorsedBy,
        endorsedDate,
        remarks: remarks.trim() === '' ? null : remarks.trim(),
      });

      let fileFailed: string | null = null;

      if (files[0]) {
        try {
          saved = await deliberationApi.attachEndorsementFile(postingId, applicantId, files[0]);
        } catch (caught) {
          fileFailed = caught instanceof ApiError ? caught.message : 'the file could not be attached';
        }
      }

      return { saved, fileFailed };
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('The endorsement could not be saved.', 0);
      setError(failure);
      // RETURNED as well as stored: the caller's own `error` is still the previous render's value.
      return { failure };
    } finally {
      setSaving(false);
    }
  }

  return {
    endorsedBy,
    setEndorsedBy,
    endorsedDate,
    setEndorsedDate,
    remarks,
    setRemarks,
    files,
    setFiles,
    saving,
    error,
    valid,
    seed,
    submit,
  };
}

export type EndorsementForm = ReturnType<typeof useEndorsementForm>;
